import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/components/theme-provider";
import FeedbackForm from "@/components/feedback-form";
import { BrandLogo } from "@/components/brand-logo";
import {
  Sun,
  Moon,
  Bell,
  Menu,
  LogOut,
  Settings,
  User,
  BookOpen,
  Code,
  Trophy,
  CreditCard,
  FileText,
  Users,
  HelpCircle,
  ChevronDown,
  MapPin,
  MessageSquare,
  Rocket,
} from "lucide-react";

const primaryNavigationItems = [
  { name: "Courses", href: "/courses", icon: BookOpen },
  { name: "Starter Kits", href: "/starter-kits", icon: Rocket },
  { name: "Practice", href: "/playground", icon: Code },
  { name: "Career Hub", href: "/career", icon: Users },
];

const exploreNavigationItems = [
  { name: "Certifications", href: "/certifications", icon: Trophy },
  { name: "Roadmap", href: "/roadmap", icon: MapPin },
  { name: "Resources", href: "/documentation", icon: FileText },
  { name: "Support", href: "/help", icon: HelpCircle },
];

const navLinkBase =
  "flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[13px] font-medium transition-colors xl:text-sm";

export default function Navigation() {
  const [location] = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { setTheme, actualTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const mobileNavigationItems = [
    ...primaryNavigationItems,
    ...exploreNavigationItems,
  ];

  const getUserInitials = (currentUser: any) => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName[0]}${currentUser.lastName[0]}`;
    }
    return currentUser?.username?.[0]?.toUpperCase() || "U";
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "student":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "professional":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      case "enterprise":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const isActive = (href: string) => location === href;

  const getNavLinkClassName = (href: string) =>
    `${navLinkBase} ${
      isActive(href)
        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-cyan-300"
        : "text-gray-700 hover:bg-gray-100 hover:text-blue-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-cyan-300"
    }`;

  const handleLogout = async () => {
    try {
      setMobileMenuOpen(false);
      await logout();
    } catch (error) {
      // Logout errors are surfaced by the auth hook when available.
    }
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-md dark:border-gray-800 dark:bg-gray-950/95">
      <div className="mx-auto flex h-14 max-w-7xl items-center px-3 sm:px-6 lg:px-8">
        <Link
          href="/"
          className="flex min-w-0 shrink-0 items-center rounded-md py-1 pr-2 transition-opacity hover:opacity-90"
          aria-label="AI Academy Pro home"
        >
          <BrandLogo
            stacked={false}
            markClassName="h-8 w-8 sm:h-9 sm:w-9"
            textClassName="max-w-[9rem] sm:max-w-[11rem]"
          />
        </Link>

        <div className="hidden min-w-0 flex-1 items-center justify-center gap-1 px-2 lg:flex">
          {primaryNavigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.name}
                href={item.href}
                className={getNavLinkClassName(item.href)}
              >
                <IconComponent className="h-4 w-4 shrink-0" />
                <span>{item.name}</span>
              </Link>
            );
          })}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 gap-1.5 px-2.5 text-[13px] font-medium text-gray-700 hover:text-blue-700 dark:text-gray-300 dark:hover:text-cyan-300 xl:text-sm"
              >
                Explore
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-52">
              {exploreNavigationItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link href={item.href} className="flex w-full items-center">
                      <IconComponent className="mr-2 h-4 w-4" />
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => setTheme(actualTheme === "dark" ? "light" : "dark")}
            aria-label="Toggle theme"
          >
            {actualTheme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {isAuthenticated ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="hidden sm:inline-flex"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="" alt={user?.username} />
                      <AvatarFallback className="bg-blue-600 text-sm text-white">
                        {getUserInitials(user)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden text-left md:block">
                      <p className="max-w-32 truncate text-sm font-medium">
                        {user?.firstName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.username}
                      </p>
                      <Badge
                        variant="outline"
                        className={`text-xs ${getTierColor(user?.tier || "guest")}`}
                      >
                        {user?.tier
                          ? `${user.tier.charAt(0).toUpperCase()}${user.tier.slice(1)} Tier`
                          : "Guest Tier"}
                      </Badge>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link href="/dashboard" className="flex w-full items-center">
                      <BookOpen className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/profile" className="flex w-full items-center">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/settings" className="flex w-full items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  {user?.tier !== "enterprise" && (
                    <DropdownMenuItem asChild>
                      <Link href="/payment-plans" className="flex w-full items-center">
                        <CreditCard className="mr-2 h-4 w-4" />
                        Upgrade Plan
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1">
                    <FeedbackForm
                      triggerButton={
                        <button className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground">
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Report Issue
                        </button>
                      }
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex w-full items-center text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Link href="/login">
                <Button variant="ghost" size="sm" className="h-9 px-3 text-[13px] xl:text-sm">
                  Sign In
                </Button>
              </Link>
              <Link href="/register">
                <Button size="sm" className="h-9 px-3 text-[13px] xl:text-sm">
                  Get Started
                </Button>
              </Link>
            </div>
          )}

          <div className="lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open navigation menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[min(22rem,calc(100vw-2rem))] p-0"
              >
                <SheetTitle className="sr-only">AI Academy Pro navigation</SheetTitle>
                <SheetDescription className="sr-only">
                  Browse courses, starter kits, practice tools, career resources, and account actions.
                </SheetDescription>
                <div className="flex h-full flex-col">
                  <div className="border-b px-5 py-5">
                    <BrandLogo markClassName="h-10 w-10" />
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-1">
                      {mobileNavigationItems.map((item) => {
                        const IconComponent = item.icon;
                        return (
                          <Link
                            key={item.name}
                            href={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 rounded-lg px-4 py-3 transition-colors ${
                              isActive(item.href)
                                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-cyan-300"
                                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                            }`}
                          >
                            <IconComponent className="h-5 w-5 shrink-0" />
                            <span className="font-medium">{item.name}</span>
                          </Link>
                        );
                      })}
                    </div>

                    <div className="my-5 border-t border-gray-200 dark:border-gray-800" />

                    {isAuthenticated ? (
                      <div className="space-y-1">
                        <Link
                          href="/dashboard"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 rounded-lg px-4 py-3 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                        >
                          <BookOpen className="h-5 w-5" />
                          <span className="font-medium">Dashboard</span>
                        </Link>
                        <Link
                          href="/profile"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 rounded-lg px-4 py-3 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                        >
                          <User className="h-5 w-5" />
                          <span className="font-medium">Profile</span>
                        </Link>
                        <Link
                          href="/settings"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 rounded-lg px-4 py-3 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                        >
                          <Settings className="h-5 w-5" />
                          <span className="font-medium">Settings</span>
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <LogOut className="h-5 w-5" />
                          <span className="font-medium">Log out</span>
                        </button>
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        <Link
                          href="/login"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Button variant="outline" className="w-full">
                            Sign In
                          </Button>
                        </Link>
                        <Link
                          href="/register"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Button className="w-full">Get Started</Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
